import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Card, CardActions, CardContent, TextField, Typography, Box, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';

import { GroupsContext } from '../context';
import { translate as t } from 'hooks/useTranslations';
import { Form } from 'components/_forms';
import { Group } from 'views/App/Portal/LexID/Groups/_types/group';
import { type ServerErrorResponse } from 'data_sources/constants';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import errorHelper from 'helpers/errorHelper';

const NewGroup: React.FC = () => {
  const navigate = useNavigate();
  const { createGroup, groupSchema } = useContext(GroupsContext);
  const [serverErrors, setServerErrors] = useState<ServerErrorResponse>();
  const [isSaving, setIsSaving] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    trigger,
  } = useForm<Group>({
    values: {
      name: '',
      description: '',
    },
    resolver: yupResolver(groupSchema),
  });

  const handleSave: SubmitHandler<Group> = (values: Group) => {
    setIsSaving(true);
    createGroup(
      values,
      (guid: string) => navigate('..'),
      (errors: ServerErrorResponse) => {
        setIsSaving(false);
        setServerErrors(errors);
      }
    );
  };

  return (
    <TakeoverLayout
      pageTitle={t('groups.new.title')}
      backUrl='..'
    >
      <Card
        sx={{ maxWidth: 800, mx: 'auto', width: '100%' }}
        className='lex-form'
      >
        <Form onSubmit={handleSubmit(handleSave)}>
          <CardContent>
            {errorHelper.renderAllErrors(serverErrors)}
            <Grid
              container
              columnSpacing={4}
              rowGap={6}
            >
              <Grid
                size={{ xs: 12, sm: 12 }}
              >
                <TextField
                  id='name'
                  key='name'
                  label={t('groups.properties.name')}
                  variant='outlined'
                  required
                  autoFocus
                  fullWidth
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name ? errors.name.message : undefined}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 12 }}
              >
                <TextField
                  id='description'
                  key='description'
                  label={t('groups.properties.description')}
                  variant='outlined'
                  fullWidth
                  multiline
                  {...register('description')}
                  error={!!errors.description}
                  helperText={errors.description ? errors.description.message : undefined}
                />
              </Grid>
            </Grid>
          </CardContent>
          <CardActions>
            <Box />
            <Stack
              direction='row'
              spacing={2}
            >
              <Button
                color='neutral'
                href='..'
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={() => {
                  trigger();
                }}
                disabled={isSaving}
                component='button'
                type='submit'
                variant='contained'
              >
                {t('groups.new.save')}
              </Button>
            </Stack>
          </CardActions>
        </Form>
      </Card>
    </TakeoverLayout>
  );
};

export default NewGroup;
